import { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useGetMeAgreements } from '@/api/auth/self/self';
import { useLanguage } from '@/contexts/LanguageContext';
import { LegalDocumentViewDialog } from '@/components/legal/LegalDocumentViewDialog';
import { UserAgreementStatus } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Settings card that displays the status of legal documents (Privacy Policy, Terms of Use).
 * Shows the current version, accepted version, and acceptance date for each document.
 * Provides a button to view each document in a dialog.
 */
export function LegalDocumentsSettingsCard() {
  const { t, language } = useLanguage();
  const [viewingDocument, setViewingDocument] = useState<{
    documentType: string;
    version: string;
  } | null>(null);

  const { data: agreementsResponse, isLoading } = useGetMeAgreements();

  const locale = useMemo(() => {
    switch (language) {
      case 'fr':
        return 'fr-FR';
      case 'de':
        return 'de-DE';
      case 'es':
        return 'es-ES';
      default:
        return 'en-US';
    }
  }, [language]);

  /**
   * Format a date string to a localized date
   */
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Get localized document name
   */
  const getDocumentName = (documentType: string) => {
    if (documentType === 'privacy-policy') {
      return t.settings.legalDocuments.privacyPolicy;
    }
    if (documentType === 'terms-of-use') {
      return t.settings.legalDocuments.termsOfUse;
    }
    return documentType;
  };

  /**
   * Handle opening a document for viewing
   */
  const handleViewDocument = (agreement: UserAgreementStatus) => {
    setViewingDocument({
      documentType: agreement.documentType,
      version: agreement.currentVersion,
    });
  };

  /**
   * Handle closing the document view dialog
   */
  const handleCloseDialog = () => {
    setViewingDocument(null);
  };

  /**
   * Render a single agreement row
   */
  const renderAgreementRow = (agreement: UserAgreementStatus) => {
    const acceptedDate = formatDate(agreement.acceptedUtc);

    return (
      <div
        key={agreement.documentType}
        className="flex items-start justify-between gap-4 py-3 border-b last:border-b-0"
      >
        <div className="flex-1 min-w-0">
          <div className="font-medium">{getDocumentName(agreement.documentType)}</div>
          <div className="text-sm text-muted-foreground space-y-0.5">
            <div>
              {t.settings.legalDocuments.currentVersion}: {agreement.currentVersion}
            </div>
            {agreement.acceptedVersion && acceptedDate ? (
              <div>
                {t.settings.legalDocuments.acceptedOn} {acceptedDate} (v{agreement.acceptedVersion})
              </div>
            ) : (
              <div>{t.settings.legalDocuments.neverAccepted}</div>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => { handleViewDocument(agreement); }}>
          {t.settings.legalDocuments.view}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t.settings.legalDocuments.title}</CardTitle>
          <CardDescription>{t.settings.legalDocuments.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="divide-y">
              {agreementsResponse?.data.agreements.map((agreement) =>
                renderAgreementRow(agreement)
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {viewingDocument && (
        <LegalDocumentViewDialog
          open={true}
          onClose={handleCloseDialog}
          documentType={viewingDocument.documentType}
          version={viewingDocument.version}
        />
      )}
    </>
  );
}
